import DescriptionQuestionRender from 'components/ZentakeForm/Questions/DescriptionQuestionRender'
import MatrixCheckboxRender from 'components/ZentakeForm/Questions/MatrixCheckboxRender'
import MatrixOpenEndedRender from 'components/ZentakeForm/Questions/MatrixOpenEndedRender'
import MatrixRadioRender from 'components/ZentakeForm/Questions/MatrixRadioRender'
import NumberQuestionRender from 'components/ZentakeForm/Questions/NumberQuestionRender'
import TableOpenEndedRender from 'components/ZentakeForm/Questions/TableOpenEndedRender'
import React from 'react'
import HandoutQuestionRenderer from 'ui/HandoutQuestionRenderer'
import HorizontalCheckboxQuestionRenderer from 'ui/HorizontalCheckboxQuestionRenderer'
import HorizontalRadioQuestionRenderer from 'ui/HorizontalRadioQuestionRenderer'
import ImageMarker from 'ui/ImageMarker'
import DateField from 'ui/molecules/DateField'
import DraggableFileUploadField from 'ui/molecules/DraggableFileUploadField'
import PhoneField from 'ui/molecules/PhoneField'
import RadioField from 'ui/molecules/RadioField'
import TextAreaField from 'ui/molecules/TextAreaField'
import TextField from 'ui/molecules/TextField'
import TimeField from 'ui/molecules/TimeField'
import SignatureQuestionRender from 'ui/SignatureQuestionRender'
import SingleLineName from 'ui/SingleLineName'
import StripeCard from 'ui/StripeCard'
import { QuestionTitle } from 'ui/Text'
import VerticalCheckboxQuestionRenderer from 'ui/VerticalCheckboxQuestionRenderer'

import Divider from '@/ui/atoms/Divider'
import { DropdownQuestionRender } from '@/ui/DropdownQuestionRender'

function UnknownQuestion() {
  return null
}

export function BuilderQuestionFactory({ question, addRowToTableInBuilder }) {
  if (question.question_type.question_type === 'radio') {
    if (question.question_type.question_subtype === 'vertical_list') {
      return (
        <RadioField
          choices={question.question_choices.map((choice) => ({
            label: choice.text_value,
            value: choice?.id?.toString(),
          }))}
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          numberOfColumns={question.num_columns}
        />
      )
    }
    if (question.question_type.question_subtype === 'horizontal_list') {
      return (
        <HorizontalRadioQuestionRenderer
          getValue={(choiceId) =>
            question.default_value?.split(',')?.includes(choiceId.toString()) ||
            false
          }
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          num_columns={question.num_columns}
          questionChoices={question.question_choices}
        />
      )
    }
    if (
      ['dropdown', 'dropdown_multiple'].includes(
        question.question_type.question_subtype
      )
    ) {
      return <DropdownQuestionRender {...{ question }} />
    }
  }

  if (question.question_type.question_type === 'text') {
    if (
      question.question_type.question_subtype === 'single_line' ||
      question.question_type.question_subtype === 'email' ||
      question.question_type.question_subtype === 'calculation'
    ) {
      return (
        <TextField
          disabled
          useEvent
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          name={question.id}
          placeholder={question.placeholder}
          value={question.default_value || ''}
        />
      )
    }
    if (question.question_type.question_subtype === 'multi_line') {
      return (
        <TextAreaField
          disabled
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          name={question.id}
          placeholder={question.placeholder}
          value={question.default_value || ''}
        />
      )
    }
    if (question.question_type.question_subtype === 'phone') {
      return (
        <PhoneField
          disabled
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          name={question.id}
          placeholder={question.placeholder}
          value={question.default_value || ''}
        />
      )
    }
    if (question.question_type.question_subtype === 'full_name') {
      return (
        <SingleLineName
          disabled
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          name={question.id}
          placeholder={question.placeholder}
          showMiddleName={!!question.include_middle_name}
        />
      )
    }
  }

  if (question.question_type.question_type === 'checkbox') {
    if (question.question_type.question_subtype === 'vertical_list') {
      return (
        <VerticalCheckboxQuestionRenderer
          getValue={(choiceId) =>
            question.default_value?.split(',')?.includes(choiceId.toString())
          }
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          num_columns={question.num_columns}
          question={question}
          questionChoices={question.question_choices}
        />
      )
    }
    if (question.question_type.question_subtype === 'horizontal_list') {
      return (
        <HorizontalCheckboxQuestionRenderer
          getValue={(choiceId) =>
            question.default_value?.split(',')?.includes(choiceId.toString())
          }
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          question={question}
          questionChoices={question.question_choices}
        />
      )
    }
  }

  if (question.question_type.question_type === 'datetime') {
    if (
      ['date_only', 'month_year', 'datetime'].includes(
        question.question_type.question_subtype
      )
    ) {
      return (
        <div>
          <QuestionTitle
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
          >
            {question.text}
          </QuestionTitle>
          <DateField
            disabled
            disabledGrayed
            semibold
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
            picker={
              question.question_type.question_subtype === 'month_year'
                ? 'month'
                : null
            }
            showTime={question.question_type.question_subtype === 'datetime'}
            value={question.default_value || ''}
          />
        </div>
      )
    }
    if (question.question_type.question_subtype === 'time_only') {
      return (
        <div>
          <QuestionTitle
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
          >
            {question.text}
          </QuestionTitle>
          <TimeField
            disabled
            disabledGrayed
            semibold
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
            value={question.default_value || ''}
          />
        </div>
      )
    }
  }

  if (question.question_type.question_type === 'number') {
    if (question.question_type.question_subtype === 'number') {
      return (
        <NumberQuestionRender
          readonly
          isOfficeUse={question.is_office_use_only}
          is_required={question.is_required}
          label={question.text}
          value={question.default_value || ''}
        />
      )
    }
  }

  if (question.question_type.question_type === 'file_upload') {
    if (question.question_type.question_subtype === 'file_upload') {
      if (question.multi_file_upload) {
        return (
          <DraggableFileUploadField
            disabled
            multiple
            semibold
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
            label={question.text}
          >
            {question.text}
          </DraggableFileUploadField>
        )
      }

      return (
        <TextField
          disabled
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          type="file"
        />
      )
    }
    if (question.question_type.question_subtype === 'marker') {
      return (
        <div>
          <QuestionTitle
            isOfficeUse={question.is_office_use_only}
            isRequired={question.is_required}
          >
            {question.text}
          </QuestionTitle>
          <ImageMarker
            disabled
            imageBase={question?.imageToDisplayOnBuilder}
            label={question.text}
            noImageText="Please add an image"
          />
        </div>
      )
    }
    if (question.question_type.question_subtype === 'handout') {
      return (
        <HandoutQuestionRenderer
          // file={question?.imageToDisplayOnBuilder}
          helpText={question.help_text}
          isOfficeUse={question.is_office_use_only}
          isReadOnly={question.is_read_only}
          isRequired={question.is_required}
          label={question.text}
        />
      )
    }
  }

  if (question.question_type.question_type === 'signature') {
    // TODO: when we have more types of signatures, this should be adjusted.
    // Be wary of all preexisting subtypes for signatuers.

    return (
      <SignatureQuestionRender
        displayOnly
        forceSignatureLine
        showConsent
        isOfficeUse={question.is_office_use_only}
        isRequired={question.is_required}
        label={question.text}
        subtype={question.question_type.question_subtype}
      />
    )
    // }
  }

  if (question.question_type.question_type === 'matrix') {
    if (question.question_type.question_subtype === 'radio') {
      return (
        <MatrixRadioRender
          readonly
          help_text={question.help_text}
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          question_columns={question.question_columns}
          question_rows={question.question_rows}
        />
      )
    }
    if (question.question_type.question_subtype === 'checkbox') {
      return (
        <MatrixCheckboxRender
          readonly
          help_text={question.help_text}
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          question_columns={question.question_columns}
          question_rows={question.question_rows}
        />
      )
    }
    if (question.question_type.question_subtype === 'open_ended') {
      return (
        <MatrixOpenEndedRender
          readonly
          help_text={question.help_text}
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          question_columns={question.question_columns}
          question_rows={question.question_rows}
        />
      )
    }
  }

  if (question.question_type.question_type === 'display_only') {
    if (question.question_type.question_subtype === 'markdown') {
      return (
        <DescriptionQuestionRender
          withOverlay
          key={question.text}
          value={question.text}
        />
      )
    }
  }

  if (question.question_type.question_type === 'summary') {
    return (
      <DescriptionQuestionRender
        withOverlay
        key={question.text}
        value={question.text}
      />
    )
  }

  if (question.question_type.question_type === 'table') {
    if (question.question_type.question_subtype === 'open_ended') {
      return (
        <TableOpenEndedRender
          builderMode
          readonly
          addRowToTableInBuilder={addRowToTableInBuilder}
          help_text={question.help_text}
          isOfficeUse={question.is_office_use_only}
          isRequired={question.is_required}
          label={question.text}
          question_columns={question.question_columns}
          question_rows={question.question_rows}
        />
      )
    }
  }

  if (question.question_type?.question_type === 'credit_card') {
    if (question.question_type?.question_subtype === 'stripe') {
      return (
        <StripeCard
          disabled
          isRequired={question.is_required}
          label={question.text}
        />
      )
    }
  }

  if (question.question_type?.question_type === 'divider') {
    return <Divider />
  }

  return <UnknownQuestion />
}

export default BuilderQuestionFactory
